import { ReactNode, useState } from 'react';

import { Box, Spinner, Tooltip } from '@chakra-ui/react';
import { MdOutlineLockClock, MdSmartToy } from 'react-icons/md';

import { Deal } from '../../../gql/dealGql';
import {
  DealLockStatus,
  Deal as GeneratedDeal,
  useOnDealLockStatusSubscription,
} from '../../../gql/generated/graphql';

import { getIsSecureLinkActive } from '../../../utils/customers';

type TooltipContent = {
  icon: ReactNode;
  label: string;
};

const tooltipContentMap: Record<DealLockStatus, TooltipContent> = {
  [DealLockStatus.AiCall]: {
    icon: <MdSmartToy size={20} color="gray" />,
    label: 'AI call in progress',
  },
  [DealLockStatus.CustomerEntry]: {
    icon: <MdOutlineLockClock size={20} color="gray" />,
    label: 'Locked for customer entry',
  },
  [DealLockStatus.MovingToClosing]: {
    icon: <Spinner size="sm" color="gray" />,
    label: 'Moving deal to closing',
  },
};

type TaskLockedTooltipProps = {
  deal:
    | Pick<Deal, 'id' | 'lock_status' | 'customer'>
    | Pick<GeneratedDeal, 'id' | 'lock_status' | 'customer'>;
  secureLinkFlag: boolean;
};

const DealLockedTooltip = ({ deal, secureLinkFlag }: TaskLockedTooltipProps) => {
  const lockedForCustomerEntry = getIsSecureLinkActive(secureLinkFlag, deal.customer);
  const [isLocked, setIsLocked] = useState(deal.lock_status != null);

  // Listen for changes to the deal's lock_status to live lock/unlock the deal
  useOnDealLockStatusSubscription({
    skip: !deal.id,
    variables: { dealId: Number(deal.id) },
    onData: ({ data }) => {
      if (!data.data?.onDealLockStatus) {
        return;
      }
      setIsLocked(data.data.onDealLockStatus.lockStatus != null);
    },
  });

  if (!isLocked && !lockedForCustomerEntry) {
    return null;
  }

  const tooltipContent = lockedForCustomerEntry
    ? tooltipContentMap[DealLockStatus.CustomerEntry] // Customer entry doesn't currently use lock_status
    : tooltipContentMap[deal.lock_status as DealLockStatus];

  return (
    <Tooltip
      label={tooltipContent.label}
      placement="top"
      px={2}
      py={1}
      borderRadius="md"
      bg="gray.600"
    >
      <Box>{tooltipContent.icon}</Box>
    </Tooltip>
  );
};

export default DealLockedTooltip;
